import React, { useMemo, useState } from 'react';
import { MessageCircle, ChevronRight, Sparkles } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { usePmUnits, leaseBook, portfolioDldValue, portfolioRent, isOffplanUnit } from './portfolioMetrics';
import './CopilotBriefing.css';

// ============================================================
// THE DAILY BRIEFING of InvestMenter-KI, the owner's "AI Wealth Co-Pilot"
// (Michael's concept, 2026-10-15): the AI speaks first. Not "how can I help
// you?" but what is happening with the portfolio, where to look, and what to
// consider — with a traffic light per property.
//
// Deterministic on purpose. Every figure is built here from the SAME helpers
// the rest of Home uses (portfolioDldValue, portfolioRent, leaseBook), so the
// briefing can never contradict the cards below it and contains no figure a
// model made up. The conversation it opens is where the AI explains.
//
// Honesty rules, from the product's history:
//   - a unit without rent on record is a DATA GAP ("no rent recorded"), not a
//     problem with the property and not "vacant": Property Management does not
//     hold every lease. Counted on its own line, never as "needs attention";
//   - DLD values of LOW confidence never drive a warning or an opportunity;
//     an OPPORTUNITY states the valuation's confidence (high / medium);
//   - units we cannot judge are "no issues found", not "on plan".
// Brand: gold #D6B160, black, white (alpha allowed). The ONE exception is the
// traffic light itself, which is real green / amber / light: the user asked
// for it (2026-10-15) and a traffic light only reads as one in those colours.
// ============================================================

interface Props {
  investorName?: string;
  investorId?: string;
  units: any[];
  dldIntel: any;
  pmUnitsOverride?: any[];
  /** Opens the InvestMenter-KI conversation with this message. */
  onAsk: (message: string) => void;
  /** "View analysis"; the button is left out when there is nowhere to go. */
  onOpenPortfolio?: () => void;
  /** "Gesamtvermögen" — the whole wealth, beyond Dubai. */
  onOpenWealth?: () => void;
}

type Kind = 'attention' | 'opportunity' | 'gap';
interface Point { unitId: string; kind: Kind; rank: number; title: string; body: string; ask: string }

const LOW_CONF = ['LOW', 'INSUFFICIENT_DATA'];

// INCOME-FIRST accounts (owner decision 2026-11-04, Dada only): an office
// investor whose prices bought tenancies and furnishing, which DLD's empty-unit
// sales never reflect, so the card read "-21.9%" every morning. For these
// accounts, below purchase price, the card shows the rent the signed leases pay
// instead of the value change, and drops the "below purchase price" notes. The
// comparison stays in View analysis. Accounts are listed by an FNV-1a hash of
// the lower-cased investor id, so no client e-mail sits in the public bundle.
const INCOME_FIRST = new Set(['d036e291']);
const fnv1a = (s: string) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, '0');
};
const LEASE_SOON_DAYS = 60;

const CopilotBriefing: React.FC<Props> = ({ investorName, investorId, units, dldIntel, pmUnitsOverride, onAsk, onOpenPortfolio, onOpenWealth }) => {
  const { t, language } = useLanguage();
  const de = language === 'de';
  const locale = de ? 'de-DE' : 'en-AE';
  const aed = (n: number) => `${de ? '' : 'AED '}${Math.round(n).toLocaleString(locale)}${de ? ' AED' : ''}`;
  const pct = (n: number) => `${Math.abs(n).toLocaleString(locale, { maximumFractionDigits: 1 })}${de ? ' %' : '%'}`;
  const short = (n: number) => {
    const v = Math.abs(n) >= 1e6
      ? `${(n / 1e6).toLocaleString(locale, { maximumFractionDigits: 2 })} ${de ? 'Mio.' : 'M'}`
      : Math.round(n).toLocaleString(locale);
    return de ? `${v} AED` : `AED ${v}`;
  };
  const [showAll, setShowAll] = useState(false);
  const [showYieldHow, setShowYieldHow] = useState(false);
  const incomeFirst = INCOME_FIRST.has(fnv1a(String(investorId || '').trim().toLowerCase()));

  const pmUnits = usePmUnits(investorId, pmUnitsOverride);
  // The yield is on TOTAL investment wherever purchase costs are on record
  // (leaseBook adds units/{id}.purchaseCostsExVat, excl. VAT — recoverable for
  // a VAT-registered commercial landlord), the way the team's purchase sheets
  // state it. For income-first accounts the note names units without them.
  const book = useMemo(() => leaseBook(pmUnits), [pmUnits]);
  const costsMissing = incomeFirst
    ? book.rows.filter((r: any) => !(Number(pmUnits.find((u: any) => String(u?.id) === String(r.unitId))?.purchaseCostsExVat) > 0)).length
    : 0;
  const valueInfo = useMemo(() => portfolioDldValue(dldIntel, units), [dldIntel, units]);
  const rentInfo = useMemo(() => portfolioRent(dldIntel, units), [dldIntel, units]);

  const { points, calm } = useMemo(() => {
    const leaseById = new Map(book.rows.map((r: any) => [String(r.unitId), r]));
    const rentById = new Map(rentInfo.lines.map((l: any) => [String(l.unit?.id), l]));
    const pts: Point[] = [];
    const flagged = new Set<string>();
    valueInfo.rows.forEach((row: any) => {
      const u = row.unit;
      const id = String(u?.id);
      const name = u?.name || u?.project || (de ? 'Einheit' : 'Unit');
      const paid = Number(u?.purchaseValue) || 0;
      const conf = String(row.confidence || '').toUpperCase();
      const dldOk = row.source === 'dld' && !LOW_CONF.includes(conf);
      const gain = dldOk && paid > 0 ? ((row.value - paid) / paid) * 100 : null;
      const offplan = isOffplanUnit(u);
      const lease: any = leaseById.get(id);
      const rentLine: any = rentById.get(id);

      if (lease && lease.daysLeft >= 0 && lease.daysLeft <= LEASE_SOON_DAYS) {
        pts.push({
          unitId: id, kind: 'attention', rank: 1, title: name,
          body: de
            ? `Der Mietvertrag endet in ${lease.daysLeft} Tagen. Jetzt Verlängerung oder Neuvermietung klären.`
            : `The lease ends in ${lease.daysLeft} days. Time to settle renewal or re-letting.`,
          ask: de ? `Der Mietvertrag meiner Einheit ${name} endet in ${lease.daysLeft} Tagen. Was empfehlen Sie?` : `The lease of my unit ${name} ends in ${lease.daysLeft} days. What do you recommend?`,
        });
        flagged.add(id);
      }
      if (lease && lease.netYield !== null && lease.netYield < 3) {
        pts.push({
          unitId: id, kind: 'attention', rank: 2, title: name,
          body: de
            ? `Netto-Mietrendite ${pct(lease.netYield)} auf den Kaufpreis, unter der 3-%-Schwelle. Kosten und Miete prüfen lohnt sich.`
            : `Net rental yield ${pct(lease.netYield)} on the purchase price, below the 3% line. Worth checking costs and rent.`,
          ask: de ? `Meine Einheit ${name} hat eine Netto-Mietrendite von ${pct(lease.netYield)}. Woran liegt das und was kann ich tun?` : `My unit ${name} has a net rental yield of ${pct(lease.netYield)}. Why, and what can I do?`,
        });
        flagged.add(id);
      }
      // An income-first account is not told daily that it sits below its
      // purchase price (see INCOME_FIRST); View analysis still shows it.
      if (!incomeFirst && gain !== null && gain <= -5) {
        pts.push({
          unitId: id, kind: 'attention', rank: 3, title: name,
          body: de
            ? `Der DLD-Schätzwert liegt ${pct(gain)} unter dem Kaufpreis (${aed(row.value)} zu ${aed(paid)}). Kein Grund zur Eile, aber wir behalten das im Blick.`
            : `The DLD estimate is ${pct(gain)} below the purchase price (${aed(row.value)} vs ${aed(paid)}). No reason to rush, but we are watching it.`,
          ask: de ? `Der DLD-Schätzwert meiner Einheit ${name} liegt ${pct(gain)} unter dem Kaufpreis. Wie ist das einzuordnen?` : `The DLD estimate of my unit ${name} is ${pct(gain)} below the purchase price. How should I read that?`,
        });
        flagged.add(id);
      }
      if (!offplan && !lease && rentLine && rentLine.rentSource === 'none' && row.marketRent > 0) {
        pts.push({
          unitId: id, kind: 'gap', rank: 4, title: name,
          body: de
            ? `Keine Miete erfasst. Laut DLD-Mietverträgen sind für vergleichbare Einheiten rund ${aed(row.marketRent)} im Jahr üblich.`
            : `No rent recorded. Registered DLD rent contracts put comparable units at about ${aed(row.marketRent)} a year.`,
          ask: de ? `Für meine Einheit ${name} ist keine Miete erfasst; laut DLD wären rund ${aed(row.marketRent)} im Jahr üblich. Wie gehe ich am besten vor?` : `No rent is recorded for my unit ${name}; DLD shows about ${aed(row.marketRent)} a year for comparable units. How should I proceed?`,
        });
      }
      if (gain !== null && gain >= 25 && !offplan && (conf === 'HIGH' || conf === 'MEDIUM')) {
        // The valuation's confidence is part of the sentence: the owner sees
        // the opportunity AND how solid the number behind it is.
        const sure = conf === 'HIGH' ? (de ? 'hoch' : 'high') : (de ? 'mittel' : 'medium');
        pts.push({
          unitId: id, kind: 'opportunity', rank: 5, title: name,
          body: de
            ? `Laut DLD-Schätzung ${pct(gain)} über dem Kaufpreis (${aed(row.value)}, Bewertungssicherheit: ${sure}). Damit kann eine Refinanzierung oder ein Verkauf interessant sein.`
            : `By the DLD estimate ${pct(gain)} above the purchase price (${aed(row.value)}, valuation confidence: ${sure}). That can make refinancing or a sale worth a look.`,
          ask: de ? `Meine Einheit ${name} liegt laut DLD ${pct(gain)} über dem Kaufpreis. Halten, verkaufen oder refinanzieren – was ist sinnvoll?` : `My unit ${name} is ${pct(gain)} above its purchase price by DLD. Hold, sell or refinance — what makes sense?`,
        });
      }
    });
    pts.sort((a, b) => a.rank - b.rank);
    return { points: pts, calm: valueInfo.rows.length - flagged.size };
  }, [book, rentInfo, valueInfo, de, incomeFirst]);

  if (!valueInfo.rows.length) return null;

  const attention = points.filter(p => p.kind === 'attention');
  const opportunities = points.filter(p => p.kind === 'opportunity');
  const gaps = points.filter(p => p.kind === 'gap');
  const attentionUnits = new Set(attention.map(p => p.unitId)).size;
  const ordered = [...attention, ...opportunities, ...gaps];
  const shown = showAll ? ordered : [...attention.slice(0, 2), ...opportunities.slice(0, 1)];
  const hiddenCount = ordered.length - shown.length;

  const hour = new Date().getHours();
  const first = String(investorName || '').trim().split(/\s+/)[0];
  // Same hours and texts as the header greeting (MainDashboard getGreeting),
  // so the two can never say "afternoon" and "evening" at once.
  const greetKey = hour < 12 ? 'goodMorning' : hour < 17 ? 'goodAfternoon' : 'goodEvening';
  const greet = t(greetKey);
  const hello = de ? greet : greet.charAt(0) + greet.slice(1).toLowerCase(); // "Good Evening" -> "Good evening"
  const gainAll = valueInfo.invested > 0 ? ((valueInfo.value - valueInfo.invested) / valueInfo.invested) * 100 : null;
  const anyDld = valueInfo.rows.some((r: any) => r.source === 'dld');
  const anyLease = (dldIntel?.units || []).some((x: any) => x?.valuationBasis === 'dld_plus_premium');
  // One year of each signed lease after its booked costs: the same figure the
  // yield is made of (netYield x price), so the two boxes always agree. A
  // multi-year lease's rent is its whole term, hence annualised here.
  const leaseIncome = book.rows.reduce((a: number, r: any) =>
    a + (r.netYield !== null && r.purchasePrice > 0 ? (r.netYield / 100) * r.purchasePrice
      : r.months > 0 ? (r.net * 12) / r.months : 0), 0);
  const important = attention.length + opportunities.length;
  const lead = important === 0
    ? (de ? 'Heute braucht nichts Ihre Aufmerksamkeit.' : 'Nothing needs your attention today.')
    : important === 1
      ? (de ? 'Heute gibt es einen Punkt, den Sie kennen sollten.' : 'There is one point you should know about today.')
      : (de ? `Heute gibt es ${important} Punkte, die Sie kennen sollten.` : `There are ${important} points you should know about today.`);

  const label = (k: Kind) => k === 'attention' ? (de ? 'Aufmerksamkeit' : 'Attention')
    : k === 'opportunity' ? (de ? 'Chance' : 'Opportunity') : (de ? 'Daten fehlen' : 'Data missing');

  return (
    <section className="cpb" aria-label={de ? 'Tagesbriefing der Asset Desk KI' : 'Asset Desk AI daily briefing'}>
      <div className="cpb-head">
        <span className="cpb-mark"><Sparkles size={16} /></span>
        <span className="cpb-eyebrow">{de ? 'Asset Desk KI · Ihr AI Wealth Co-Pilot' : 'Asset Desk AI · Your AI Wealth Co-Pilot'}</span>
      </div>
      <div className="cpb-hello">{first ? `${hello}, ${first}.` : `${hello}.`}</div>
      <div className="cpb-lead">{lead}</div>

      {/* three numbers instead of a sentence */}
      <div className="cpb-kpis">
        <div className="cpb-kpi">
          {/* A listed account is served DLD value + the premium its price paid
              for tenancy and fit-out (Backend/services/leaseValuation.js). */}
          <span className="cpb-kpi-k">{!anyDld ? (de ? 'Investiert' : 'Invested')
            : anyLease ? (de ? 'Marktwert' : 'Market value')
            : (de ? 'Marktwert laut DLD' : 'Market value (DLD)')}</span>
          <span className="cpb-kpi-v">{short(anyDld ? valueInfo.value : valueInfo.invested)}</span>
          {/* Why it equals the purchase price on day one: the premium his
              prices paid is carried forward and the DLD market moves it. */}
          {anyDld && anyLease && (
            <span className="cpb-kpi-s">{de ? 'Ihr Kaufpreis, fortgeschrieben mit den DLD-Marktpreisen' : 'Your purchase price, moving with DLD market prices'}</span>
          )}
        </div>
        {/* Income-first accounts below purchase price: rent instead of the
            value change (see INCOME_FIRST). */}
        {anyDld && gainAll !== null && !(incomeFirst && gainAll < 0 && leaseIncome > 0) && (
          <div className="cpb-kpi">
            <span className="cpb-kpi-k">{de ? 'Wertentwicklung' : 'Value change'}</span>
            <span className="cpb-kpi-v cpb-gold">{gainAll >= 0 ? '+' : '−'}{pct(gainAll)}</span>
            <span className="cpb-kpi-s">{de ? `auf ${short(valueInfo.invested)} investiert` : `on ${short(valueInfo.invested)} invested`}</span>
          </div>
        )}
        {anyDld && gainAll !== null && incomeFirst && gainAll < 0 && leaseIncome > 0 && (
          <div className="cpb-kpi">
            <span className="cpb-kpi-k">{de ? 'Mieteinnahmen pro Jahr' : 'Rental income per year'}</span>
            <span className="cpb-kpi-v cpb-gold">{short(leaseIncome)}</span>
            <span className="cpb-kpi-s">{de ? `aus ${book.letUnits} unterzeichneten Mietverträgen` : `from ${book.letUnits} signed leases`}</span>
          </div>
        )}
        {book.netYield !== null && book.letUnits > 0 && (
          <div className="cpb-kpi">
            {/* "Net" only when a service charge is known for every lease: with
                none on record nothing was deducted and the figure is gross. */}
            <span className="cpb-kpi-k">{book.scUnknown >= book.letUnits
              ? (de ? 'Mietrendite (vor Servicegebühren)' : 'Rental yield (before service charges)')
              : (de ? 'Netto-Mietrendite' : 'Net rental yield')}</span>
            <span className="cpb-kpi-v">{pct(book.netYield)}</span>
            <span className="cpb-kpi-s">{de ? `${book.letUnits} vermietete Einheiten` : `${book.letUnits} let units`}
              {book.scUnknown > 0 && book.scUnknown < book.letUnits
                && (de ? ` · ${book.scUnknown} ohne Servicegebühr` : ` · ${book.scUnknown} without service charge`)}</span>
            {/* How the figure is made, in words (owner request 2026-11-04):
                the same steps as leaseBook in portfolioMetrics.ts. */}
            <button type="button" className="cpb-more is-inline" aria-expanded={showYieldHow} onClick={() => setShowYieldHow(v => !v)}>
              {de ? 'Wie wird das berechnet?' : 'How is this calculated?'}
            </button>
            {showYieldHow && (
              <>
                <span className="cpb-kpi-formula">
                  {incomeFirst
                    ? (de ? '(Jahresmiete − Servicegebühren) ÷ Gesamtinvestition' : '(Annual rent − service charges) ÷ total investment')
                    : (de ? '(Jahresmiete − Servicegebühren) ÷ Kaufpreis' : '(Annual rent − service charges) ÷ purchase price')}
                </span>
                <span className="cpb-kpi-s">
                  {incomeFirst
                    ? (de
                      ? `${book.letUnits} vermietete Einheiten. Gesamtinvestition = Kaufpreis + DLD-, Makler- und Treuhändergebühren, ohne MwSt.${costsMissing > 0 ? ` Kaufnebenkosten für ${costsMissing} noch nicht erfasst.` : ''}${book.scUnknown > 0 ? ` Servicegebühr für ${book.scUnknown} noch nicht erfasst.` : ''}`
                      : `${book.letUnits} let units. Total investment = price + DLD, agency and trustee fees, excl. VAT.${costsMissing > 0 ? ` Purchase costs not yet on record for ${costsMissing}.` : ''}${book.scUnknown > 0 ? ` Service charge not yet on record for ${book.scUnknown}.` : ''}`)
                    : (de
                      ? `${book.letUnits} vermietete Einheiten, ohne Kaufnebenkosten (DLD, Makler, Treuhänder).${book.scUnknown > 0 ? ` Servicegebühr für ${book.scUnknown} noch nicht erfasst.` : ''}`
                      : `${book.letUnits} let units, excluding purchase costs (DLD, agency, trustee).${book.scUnknown > 0 ? ` Service charge not yet on record for ${book.scUnknown}.` : ''}`)}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* traffic light, in the brand colours */}
      <div className="cpb-chips">
        <span className="cpb-chip is-calm"><i className="cpb-dot is-calm" />{calm} {de ? 'ohne Auffälligkeiten' : 'no issues found'}</span>
        {attentionUnits > 0 && <span className="cpb-chip is-att"><i className="cpb-dot is-att" />{attentionUnits} {de ? 'mit Handlungsbedarf' : 'need attention'}</span>}
        {opportunities.length > 0 && <span className="cpb-chip is-opp"><i className="cpb-dot is-opp" />{opportunities.length} {de ? (opportunities.length === 1 ? 'Chance' : 'Chancen') : (opportunities.length === 1 ? 'opportunity' : 'opportunities')}</span>}
        {gaps.length > 0 && <span className="cpb-chip is-quiet">{gaps.length} {de ? 'ohne erfasste Miete' : 'without recorded rent'}</span>}
      </div>

      {shown.length > 0 && (
        <div className="cpb-points">
          {shown.map((p) => (
            <div key={`${p.unitId}-${p.rank}`} className={`cpb-point is-${p.kind}`}>
              <div className="cpb-point-top">
                <span className="cpb-point-tag">{label(p.kind)}</span>
                <span className="cpb-point-name">{p.title}</span>
              </div>
              <div className="cpb-point-body">{p.body}</div>
              <button type="button" className="cpb-point-ask" onClick={() => onAsk(p.ask)}>
                <MessageCircle size={14} /> {de ? 'Mit der KI besprechen' : 'Discuss with the AI'}
              </button>
            </div>
          ))}
        </div>
      )}
      {hiddenCount > 0 && (
        <button type="button" className="cpb-more" onClick={() => setShowAll(true)}>
          {de ? `Alle ${ordered.length} Hinweise anzeigen` : `Show all ${ordered.length} notes`}
        </button>
      )}
      {showAll && (
        <button type="button" className="cpb-more" onClick={() => setShowAll(false)}>
          {de ? 'Weniger anzeigen' : 'Show less'}
        </button>
      )}

      <div className="cpb-actions">
        <button type="button" className="cpb-btn is-gold"
          onClick={() => onAsk(de ? 'Ich möchte mit einem Asset-Desk-Berater über mein Portfolio sprechen.' : 'I would like to talk to an Asset Desk advisor about my portfolio.')}>
          {de ? 'Mit Asset Desk sprechen' : 'Talk to Asset Desk'}
        </button>
        {onOpenPortfolio && (
          <button type="button" className="cpb-btn" onClick={onOpenPortfolio}>
            {de ? 'Analyse ansehen' : 'View analysis'} <ChevronRight size={14} />
          </button>
        )}
        {onOpenWealth && (
          <button type="button" className="cpb-btn" onClick={onOpenWealth}>
            {de ? 'Gesamtvermögen' : 'Total wealth'} <ChevronRight size={14} />
          </button>
        )}
      </div>
      <div className="cpb-foot">
        <span className="cpb-tagline">{de ? 'Analysiert. Erkennt. Warnt. Empfiehlt. Sie entscheiden.' : 'Analyses. Detects. Warns. Recommends. You decide.'}</span>
        <span>{de ? 'Werte: DLD-Transaktionen und Ihre erfassten Mietverträge.' : 'Figures: DLD transactions and your recorded leases.'}</span>
      </div>
    </section>
  );
};

export default CopilotBriefing;
